import ReactGA from 'react-ga4';

import { WIDGET_EVENTS } from '../const';
import LoggerService from '../service/LoggerService';
import isGaDebugEnabled from './gaDebug';

const gaKey = process.env.REACT_APP_GA_KEY;

export const gaActionTypes = {
    messages: {
        sent: 'Message Sent',
        received: 'Message Received'
    },
    user: {
        login: 'User login',
        logout: 'User logout',
        signup: 'User signup',
        delete: 'User delete',
        startChat: 'User start chat'
    },
    payments: {
        bannerType: 'User_clicked_on_start_trial_button_in_banner',
        triggerPopup: 'User_triggered_payment_popup_in_chat',
        popupButtons: 'User_go_to_payment_page_form_popup_button',
        profileButtons: 'User_go_to_payment_page_from_the_button_on_profile_page',
        issueNotification: 'User_go_to_retry_payment_from_issue_notification_in_the_chat',
        submitPayment: {
            googlePay: 'User_clicked_on_googlePay_payment_button',
            applePay: 'User_clicked_on_applePay_payment_button',
            creditCard: 'User_submitted_credit_card_payment'
        }
    },
    // Widget event names live in src/const.js (WIDGET_EVENTS) so the
    // standalone widget bundles (v1, v2) and this React-app code share one
    // source of truth — preventing typo drift across the three locations.
    widget: {
        viewed: WIDGET_EVENTS.VIEWED,
        opened: WIDGET_EVENTS.OPENED,
        promptSelected: WIDGET_EVENTS.PROMPT_SELECTED,
        messageSent: WIDGET_EVENTS.MESSAGE_SENT,
        messageReceived: WIDGET_EVENTS.MESSAGE_RECEIVED,
        expandClicked: WIDGET_EVENTS.EXPAND_CLICKED,
        thresholdReached: WIDGET_EVENTS.THRESHOLD_REACHED,
        continueClicked: WIDGET_EVENTS.CONTINUE_CLICKED,
        signInPrompted: WIDGET_EVENTS.SIGNIN_PROMPTED,
        termsAccepted: WIDGET_EVENTS.TERMS_ACCEPTED,
        termsDeclined: WIDGET_EVENTS.TERMS_DECLINED
    },
    chatPage: {
        accountCreated: 'veda_account_created',
        signInPrompted: 'veda_signin_prompted',
        widgetChatResumed: 'veda_widget_chat_resumed',
        messageLimitReached: 'veda_message_limit_reached'
    },
    // HORO-10474: client-side API failures. Makes "requests are failing for
    // users" a queryable rate instead of an anecdote, and carries the server's
    // x-request-id so a GA4 row can be joined to a backend log line.
    api: {
        error: 'veda_api_error'
    }
};

export const gaCategoryTypes = {
    message: 'Message',
    user: 'User',
    payments: 'Payments',
    widget: 'Widget',
    chatPage: 'ChatPage',
    api: 'Api'
};

export const gaLabelTypes = {
    successful: 'Successful'
};
class GoogleAnalytics {
    constructor() {
        if (this._instance) {
            return this._instance;
        }
        this._instance = this;
    }

    init() {
        if (!gaKey) {
            LoggerService.log('GoogleAnalytics gaKey key is not available');
            return;
        }
        // HORO-10370: debug_mode surfaces events in GA4 DebugView. Custom
        // super-properties are pushed via a subsequent gtag('set') in
        // initSession() — passing them through gtagOptions on config interferes
        // with GA4's session tracking (some names like `session_id` and `source`
        // are reserved by GA4 itself; clobbering them via config suppresses
        // session_start + custom events). Trade-off: session_start won't carry
        // our Tier-1 props, but every subsequent event will.
        //
        // HORO-10459: the gate moved to `isGaDebugEnabled` so DebugView can be
        // turned on for DEPLOYED environments too. It used to read
        // `NODE_ENV !== 'production'`, which is only ever true on a local dev
        // server — every deployed build, dev included, is a production build, so
        // dev traffic never reached DebugView.
        const initOptions = isGaDebugEnabled() ? [{ trackingId: gaKey, gtagOptions: { debug_mode: true } }] : gaKey;
        ReactGA.initialize(initOptions);
    }

    eventInit(actionType, actionCategory, label = '', extraParams = {}) {
        if (!gaKey) {
            return;
        }
        ReactGA.event(actionType, {
            category: actionCategory,
            label,
            ...extraParams
        });
    }
}

export default new GoogleAnalytics();
